/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable camelcase */
import { handle } from 'redux-pack';
import {
	ADD_GRAPHICS,
	APPEND_GRAPHICS,
	EDIT_GRAPHICS,
	UPDATE_GRAPHICS,
	DELETE_GRAPHICS,
	LOAD_GRAPHICS,
	SET_CURRENT_GRAPHICS_ID,
	SHOW_GRAPHIC_MODAL,
	HIDE_GRAPHIC_MODAL,
	REMOVE_GRAPHICS,
	SET_ENABLED_GRAPHICS,
	REMOVE_SHOT_MODAL_GRAPHIC,
	BULK_EDIT_GRAPHICS,
	SYNC_GRAPHICS_REDUCER_STATE,
	PLAYING_PREVIEW_ANIMATION,
	PREPEND_GRAPHICS,
	ADD_OVERLAYS_UPLOAD_DATA,
	DELETE_OVERLAYS_UPLOAD_DATA,
	SET_ANIMATED_GRAPHICS,
	ADD_ENABLED_GRAPHIC,
	REMOVE_ENABLED_GRAPHIC,
	ADD_ANIMATED_GRAPHIC,
	HIDE_GRAPHIC_FOR_SHOWBUILDER,
	SHOW_GRAPHIC_FOR_SHOWBUILDER,
	CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER,
	OVERLAY_ENTITY_UPDATED,
	OVERLAY_ENTITY_UPDATED_DRAFT,
	CLEAR_DRAFT_OVERLAYS,
} from '../actions/graphics';
import Socket from '../../connection/socket';
import { CloudConvertUploadData, Entity } from '../../types/interfaces';
import { arrayAdd } from '../../utils/utils';
import { EntityType } from '../../types/enums';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let socket: Socket | null = null;

interface GraphicsAction {
	type: string;
	payload: any;
	id?: number;
}

export interface AnimationType {
	id?: number;
	build_in?: number;
	build_out?: number;
	in_animation?: number;
	in_direction?: number;
	in_duration?: number;
	name: string;
	out_animation?: number;
	out_direction?: number;
	out_duration?: number;
	show_for?: number;
	delay_in?: number;
	delay_out?: number;
}

export interface GraphicsShots {
	id?: number;
	shot_id: number;
	graphic_id: number;
}

export interface OverlayUploadData {
	uploadId: string;
	originalName: string;
	totalBytes: number;
	cloudConvertUploadData?: CloudConvertUploadData;
}

interface GraphicsShotsRelationalTableInfo {
	graphic_id: number // id gr_graphics
	id: number // id gr_graphics_shots
	layer_index: number // no clue
	shot_id: number // id gr_shots
}
export interface GraphicInterface {
	id?: number;
	type?: string;
	title: string;
	pos_x: number;
	pos_y: number;
	rotate: number;
	scale_x: number;
	scale_y: number;
	z_index: number;
	created_by: number;
	edited_by: number;
	created_at?: string;
	edited_at?: string;
	channel: number;
	meeting: number;
	animation: AnimationType;
	status: boolean;
	animation_css: string;
	animation_duration: number;
	animation_direction: string;
	canvas_scalar: number;
	original_name: string;
	images: string;
	loops: number | boolean;
	play_audio: number | boolean;
	is_global: number | boolean;
	upload_id: string | null;
	shots: GraphicsShotsRelationalTableInfo[];
	cloud_convert_id?: string;
	convert_status?: string;
	thumbnail_url: string;
	provider?: string | null;
	template_type?: string |null;
}

export interface ShotOverlay {
	id: number;
	isEnabledDefault: boolean;
	isEnabled?: boolean;
}

export interface ShotOverlayData {
	overlays: {
		[key: number]: ShotOverlay;
	};
	overlayIds: number[];
	globalOverlayIds: number[];
}

export interface GraphicsState {
	graphics: GraphicInterface[];
	currentGraphicId: number;
	graphicsError: string;
	loadingGraphics: boolean;
	showGraphicModal: boolean;
	error?: string;
	draggingGraphicId: number;
	shotModalGraphics: GraphicInterface[];
	enabledGraphics: number[] | null;
	isPreviewAnimationPlaying: boolean; // is the preview of an animation playing from the side drawer
	/*
		We use the graphics original filename as a key in this map, because the
		PREPEND_GRAPHICS action needs to find the graphic it is prepending and
		remove it from the map. Since the uploading map is set when we start
		uploading, we don't know anything about the graphic but its
		original_name when the upload begins and we set the map key.
		An alternative would be to
	*/
	overlaysUploadData: Array<OverlayUploadData>
	animatedGraphics?: number[];
	enabledOverlays: number[];
	animatedOverlays: number[];
	// TODO: just for debugging, remove this before merging
	allShotOverlaysEntity?: Entity | null;
	animatedOverlayIdsEntity?: Entity | null;
	disabledShowBuilderOverlays?: number[];
}

export const initialGraphicsState: GraphicsState = {
	graphics: [],
	currentGraphicId: 0,
	graphicsError: '',
	loadingGraphics: false,
	showGraphicModal: false,
	draggingGraphicId: 0,
	shotModalGraphics: [],
	enabledGraphics: null,
	isPreviewAnimationPlaying: false,
	overlaysUploadData: [],
	animatedGraphics: [],
	enabledOverlays: [],
	animatedOverlays: [],
	allShotOverlaysEntity: null,
	animatedOverlayIdsEntity: null,
	disabledShowBuilderOverlays: [],
};

const getPosData = (keep: GraphicInterface, merge: GraphicInterface) => {
	const newScaleY = merge.scale_y;
	const oldScaleY = keep.scale_y;
	const newScaleX = merge.scale_x;

	// difference in Y scale between old and new to calc expected X scale
	const scalePercY = oldScaleY / newScaleY;
	const newModalScaleX = newScaleX * scalePercY;
	return {
		pos_x: keep.pos_x,
		pos_y: keep.pos_y,
		scale_x: newModalScaleX,
		scale_y: oldScaleY,
		rotate: keep.rotate,
	};
};

const mergePositions = (
	modalGraphics: GraphicInterface[],
	newGraphics: GraphicInterface[],
) => {
	const shotModalGraphics: GraphicInterface[] = newGraphics
		.reduce((acc: GraphicInterface[], newOverlay: GraphicInterface) => {
			const modalOverlay = modalGraphics.find((g: GraphicInterface) => g.id === newOverlay.id);

			if (!modalOverlay) {
				return [...acc, newOverlay];
			}

			const posData = getPosData(modalOverlay, newOverlay);

			const returnedOverlay = {
				...newOverlay,
				...posData,
			};

			return [...acc, returnedOverlay];
		}, [] as GraphicInterface[]);
	return shotModalGraphics;
};

const reduceFunction = (newArray: GraphicInterface[], existingOverlay: GraphicInterface) => {
	if (newArray[0].id === existingOverlay.id) {
		// we found the existing overlay we set as the first item in the new array
		const newOverlay = newArray.shift();
		if (newOverlay) {
			newArray.push(newOverlay);
		}
	} else {
		newArray.push(existingOverlay);
	}
	return newArray;
};

const actions: Map<any, Function> = new Map([
	[
		ADD_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		PREPEND_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => ({
			...state,
			graphics: [action.payload, ...state.graphics],
			overlaysUploadData: state.overlaysUploadData.filter(
				(d: OverlayUploadData) => d.uploadId !== action.payload.upload_id,
			),
		}),
	],
	[APPEND_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		graphics: [
			...state.graphics,
			action.payload,
		],
	})],
	[
		EDIT_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		UPDATE_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => ({
			...state,
			graphics: state.graphics.map((graphic: GraphicInterface) => (
				graphic.id === action.payload.id ? action.payload : graphic
			)),
			shotModalGraphics: state.shotModalGraphics.map((graphic: GraphicInterface) => (
				graphic.id === action.payload.id
					? { ...action.payload, ...getPosData(graphic, action.payload) }
					: graphic
			)),
		}),
	],
	[
		DELETE_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		LOAD_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state, loadingGraphics: true }),
			finish: (state) => ({ ...state, loadingGraphics: false }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => {
				const currentEnabledGraphics: number[] | null = state.enabledGraphics
					? [...state.enabledGraphics]
					: null;
				const graphics: GraphicInterface[] = Array.isArray(action.payload)
					? action.payload
					: [];
				// Sync runs before this, only set defaults if
				const enabledGraphics = currentEnabledGraphics || graphics.reduce<number[]>(
					(idArr: number[], graphic: GraphicInterface) => {
						const { id } = graphic;

						if (id && graphic?.animation?.build_in !== 1) {
							return [...idArr, id];
						}

						return idArr;
					},
					[],
				);

				const graphicsInModal = graphics
					.reduce((acc, g) => {
						const isInModal = state.shotModalGraphics.some((modalG) => modalG.id === g.id);
						return isInModal ? [...acc, g] : acc;
					}, [] as GraphicInterface[]);

				const shotModalGraphics = mergePositions(state.shotModalGraphics, graphicsInModal);

				return {
					...state,
					graphics,
					enabledGraphics,
					shotModalGraphics,
				};
			},
		}),
	],
	[SET_CURRENT_GRAPHICS_ID, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		currentGraphicId: action.payload,
	})],
	[SHOW_GRAPHIC_MODAL, (state: GraphicsState): GraphicsState => ({
		...state,
		showGraphicModal: true,
	})],
	[HIDE_GRAPHIC_MODAL, (state: GraphicsState): GraphicsState => ({
		...state,
		showGraphicModal: false,
	})],
	[REMOVE_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		graphics: state.graphics.filter((graphic: GraphicInterface) => graphic.id !== action.payload),
	})],
	[SET_ENABLED_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		enabledGraphics: action.payload,
	})],
	[ADD_ENABLED_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const newGraphicId: number = action.payload;
		const updateEnabledGraphics: number[] = state.enabledGraphics
			? arrayAdd(state.enabledGraphics, newGraphicId)
			: [newGraphicId];

		return {
			...state,
			enabledGraphics: updateEnabledGraphics,
		};
	}],
	[REMOVE_ENABLED_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const removeGraphicId: number = action.payload;
		const updateEnabledGraphics: number[] = state.enabledGraphics
			? state.enabledGraphics.filter((graphicId) => graphicId !== removeGraphicId)
			: [];

		return {
			...state,
			enabledGraphics: updateEnabledGraphics,
		};
	}],
	[REMOVE_SHOT_MODAL_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const shotModalGraphics = state.shotModalGraphics.filter(
			(shotModalGraphic: GraphicInterface) => shotModalGraphic.id !== action.payload,
		);
		return {
			...state, shotModalGraphics,
		};
	}],
	[PLAYING_PREVIEW_ANIMATION, (state: GraphicsState, action: GraphicsAction) => ({
		...state,
		isPreviewAnimationPlaying: action.payload,
	})],
	[BULK_EDIT_GRAPHICS, (state: GraphicsState, action: GraphicsAction) => handle(state, action, {
		start: (state) => ({ ...state }),
		finish: (state) => ({ ...state }),
		failure: (state) => ({ ...state, graphicsError: 'Error editing graphics. Please try again later.' }),
		success: (state) => ({ ...state }),
	})],
	[SYNC_GRAPHICS_REDUCER_STATE, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		...action.payload,
	})],
	[
		ADD_OVERLAYS_UPLOAD_DATA,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const newUploading = state.overlaysUploadData.filter(
				(d: OverlayUploadData) => d.uploadId !== action.payload.uploadId,
			);
			return {
				...state,
				overlaysUploadData: [...newUploading, action.payload],
			};
		},
	],
	[
		DELETE_OVERLAYS_UPLOAD_DATA,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => ({
			...state,
			overlaysUploadData: state.overlaysUploadData.filter(
				(d: OverlayUploadData) => d.uploadId !== action.payload.uploadId,
			),
		}),
	],
	[SET_ANIMATED_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		animatedGraphics: action.payload,
	})],
	[ADD_ANIMATED_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const newAnimatedGraphicId: number = action.payload;
		const updateAnimatedGraphics: number[] = state.animatedGraphics
			? arrayAdd(state.animatedGraphics, newAnimatedGraphicId)
			: [newAnimatedGraphicId];

		return {
			...state,
			animatedGraphics: updateAnimatedGraphics,
		};
	}],
	[
		EntityType.allShotOverlays,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const allShotOverlays: ShotOverlayData | null = action.payload
				?.entityValue?.allShotOverlays ?? null;
			const updateEnabledOverlays: number[] | null = allShotOverlays?.overlayIds.reduce<
				number[]
			>((overlayIdArr: number[], overlayId: number) => {
				if (allShotOverlays.overlays[overlayId].isEnabled) {
					return [...overlayIdArr, overlayId];
				}

				return overlayIdArr;
			}, []) ?? null;

			return {
				...state,
				// TODO: just for debugging, remove this before merging
				allShotOverlaysEntity: action.payload,
				...(updateEnabledOverlays && { enabledOverlays: updateEnabledOverlays }),
			};
		},
	],
	[
		EntityType.animatedOverlayIds,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const animatedOverlayIds: number[] | null = action.payload
				?.entityValue?.animatedOverlayIds ?? null;

			return {
				...state,
				// TODO: just for debugging, remove this before merging
				animatedOverlayIdsEntity: action.payload,
				...(animatedOverlayIds && { animatedOverlays: animatedOverlayIds }),
			};
		},
	],
	[
		HIDE_GRAPHIC_FOR_SHOWBUILDER,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const { disabledShowBuilderOverlays = [] } = state;
			const newDisabledShowBuilderOverlays = [...disabledShowBuilderOverlays];
			const { payload } = action;
			const index = newDisabledShowBuilderOverlays.indexOf(payload);

			if (index === -1) {
				newDisabledShowBuilderOverlays.push(payload);
			}

			return {
				...state,
				disabledShowBuilderOverlays: newDisabledShowBuilderOverlays,
			};
		},
	],
	[
		SHOW_GRAPHIC_FOR_SHOWBUILDER,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const { disabledShowBuilderOverlays = [] } = state;
			const newDisabledShowBuilderOverlays = [...disabledShowBuilderOverlays];
			const { payload } = action;

			const index = newDisabledShowBuilderOverlays.indexOf(payload);
			if (index > -1) {
				newDisabledShowBuilderOverlays.splice(index, 1);
			}

			return {
				...state,
				disabledShowBuilderOverlays: newDisabledShowBuilderOverlays,
			};
		},
	],
	[
		CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER,
		(state: GraphicsState): GraphicsState => ({
			...state,
			disabledShowBuilderOverlays: [],
		}),
	],
	[
		OVERLAY_ENTITY_UPDATED,
		(state: GraphicsState, action: GraphicsAction) => ({
			...state,
			graphics: state.graphics.reduce(
				reduceFunction,
				[action.payload.entityValue],
			),
		}),
	],
	[
		OVERLAY_ENTITY_UPDATED_DRAFT,
		(state: GraphicsState, action: GraphicsAction) => ({
			...state,
			shotModalGraphics: state.shotModalGraphics.reduce(
				reduceFunction,
				[action.payload.entityValue],
			),
		}),
	],
	[
		CLEAR_DRAFT_OVERLAYS,
		(state: GraphicsState) => ({
			...state,
			shotModalGraphics: [],
		}),
	],
]);

export const GraphicsReducer = (
	// eslint-disable-next-line default-param-last
	state: GraphicsState = initialGraphicsState,
	action: GraphicsAction,
): GraphicsState => actions.get(action.type)?.(state, action) ?? state;

export function setGraphicsReducerSocket(sock: Socket): void {
	socket = sock;
}
